/**
 * Which of the two landing designs this visitor sees.
 *
 * The site ships two visual treatments of `/`, `/owners` and `/providers` for a
 * real A/B test against the September outreach traffic: `'a'` is the one-page
 * plain-talk editorial layout, `'b'` is the county-notice newsprint layout.
 * They are not separate routes. The URL, the sitemap and the per-page SEO are
 * identical either way; only the rendering differs.
 *
 * Assignment is not made here. It happens once per visitor inside
 * `initAttribution()` — explicit `?v=`, then the sticky stored value, then a
 * coin flip — and rides along on the attribution record so it reaches the lead,
 * the attribution touch and every analytics event without a second storage key
 * or a second source of truth. This module only *reads* that decision.
 *
 * Because the assignment is settled before the first render and cannot change
 * inside a session, this is a plain function rather than a hook. There is no
 * state to subscribe to: a variant switch would mean a new session.
 */

import { getAttribution } from './attribution';

export type SiteVariant = 'a' | 'b';

/** What a visitor sees when nothing has assigned them anything. */
export const DEFAULT_VARIANT: SiteVariant = 'a';

export function currentVariant(): SiteVariant {
  /*
   * `variant` is optional text on the attribution record, not a union, because
   * attribution is dropped rather than rejected when it arrives malformed. A
   * record captured before this feature shipped, or one whose `?v=` was
   * nonsense, simply carries no usable variant. So this narrows rather than
   * trusts: anything that is not exactly 'b' is the default.
   */
  return getAttribution().variant === 'b' ? 'b' : DEFAULT_VARIANT;
}
